<h1 class="text-center border-bottom pb-3">Product import flow control</h1>

<div
  *ngIf="!hasProductFactoryAccess"
  class="alert alert-warning mt-3"
>
  <h2 class="h5 mb-2">Alleen voor Product Factory</h2>
  <p class="mb-0">Raak deze instellingen niet aan tenzij je onderdeel bent van het Product Factory-team (AWS-PF).</p>
</div>

<ng-container *ngIf="hasProductFactoryAccess">
  <div
    class="row"
    [ngClass]="{ 'd-none': feedbackSettings.show }"
  >
    <div class="col-12 col-xl-10 offset-xl-1 p-3">
      <p class="mb-0">Beheer de staten van de productimport en -data workflows.</p>
      <p class="mb-3">Selecteer een nieuwe staat en voeg optioneel een opmerking toe.</p>

      <div class="row g-4 align-items-stretch">
        <div class="col-12 col-lg-6">
          <div class="card mb-4 indi-h-50">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Data-staat</h5>
              <button
                class="btn btn-sm btn-outline-secondary"
                type="button"
                (click)="loadDataState()"
                [disabled]="isDataStateLoading || isDataStateUpdating"
              >
                <span
                  *ngIf="isDataStateLoading"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                Vernieuwen
              </button>
            </div>
            <div class="card-body">
              <ng-container *ngIf="currentDataState && !isDataStateLoading; else dataStateLoading">
                <div class="row">
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Staat:</strong></p>
                    <span [ngClass]="getDataStateBadgeClass(currentDataState.state)">
                      {{ getDataStateDisplayName(currentDataState.state) }}
                    </span>
                  </div>
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Bijgewerkt op:</strong></p>
                    <p class="mb-0">
                      {{
                        currentDataState.updatedAt ? (currentDataState.updatedAt | utcDate: 'dd-MM-yyyy HH:mm') : '-'
                      }}
                    </p>
                  </div>
                </div>
                <div
                  class="row mt-3"
                  *ngIf="currentDataState.updatedBy"
                >
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Bijgewerkt door:</strong></p>
                    <p class="mb-0">{{ currentDataState.updatedBy }}</p>
                  </div>
                </div>
                <div class="row mt-3">
                  <div class="col-12">
                    <p class="mb-1"><strong>Opmerking:</strong></p>
                    <p class="mb-0">
                      {{ currentDataState.comment ? currentDataState.comment : '-geen-' }}
                    </p>
                  </div>
                </div>
              </ng-container>
              <ng-template #dataStateLoading>
                <div class="d-flex justify-content-center py-4">
                  <span class="spinner-border text-secondary"></span>
                </div>
              </ng-template>
            </div>
          </div>

          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Data-staat bijwerken</h5>
            </div>
            <div class="card-body">
              <div class="d-flex flex-column">
                <div class="form-group pb-3">
                  <label for="dataStateSelect">Nieuwe staat</label>
                  <select
                    class="form-select"
                    id="dataStateSelect"
                    [(ngModel)]="selectedDataState"
                    name="dataStateSelect"
                    [disabled]="isDataStateUpdating"
                  >
                    <option
                      [ngValue]="null"
                      disabled
                      hidden
                    >
                      Selecteer een staat
                    </option>
                    <option
                      *ngFor="let state of availableDataStates"
                      [ngValue]="state"
                      [disabled]="state === currentDataState?.state || state === productDataStateValues.ERROR"
                    >
                      {{ getDataStateDisplayName(state) }}
                      <span *ngIf="state === currentDataState?.state"> (huidig)</span>
                    </option>
                  </select>
                </div>

                <div class="form-group pb-3">
                  <label for="dataCommentInput">Opmerking (optioneel)</label>
                  <textarea
                    class="form-control"
                    id="dataCommentInput"
                    rows="3"
                    [(ngModel)]="dataStateComment"
                    name="dataComment"
                    placeholder="Voeg een opmerking toe over deze data-staatswijziging..."
                    [disabled]="isDataStateUpdating"
                  ></textarea>
                </div>

                <div class="form-group align-self-end pt-1">
                  <button
                    class="btn btn-primary"
                    type="button"
                    [disabled]="!canUpdateDataState()"
                    (click)="updateDataState()"
                  >
                    <span
                      *ngIf="isDataStateUpdating"
                      class="spinner-border spinner-border-sm me-2"
                    ></span>
                    {{ isDataStateUpdating ? 'Bijwerken...' : 'Data-staat bijwerken' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6">
          <div class="card mb-4 indi-h-50">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Importstaat</h5>
              <button
                class="btn btn-sm btn-outline-secondary"
                type="button"
                (click)="loadImportState()"
                [disabled]="isImportStateLoading || isImportStateUpdating"
              >
                <span
                  *ngIf="isImportStateLoading"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                Vernieuwen
              </button>
            </div>
            <div class="card-body">
              <ng-container *ngIf="currentImportState && !isImportStateLoading; else importStateLoading">
                <div class="row">
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Staat:</strong></p>
                    <span [ngClass]="getImportStateBadgeClass(currentImportState.state)">
                      {{ getImportStateDisplayName(currentImportState.state) }}
                    </span>
                  </div>
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Bijgewerkt op:</strong></p>
                    <p class="mb-0">
                      {{
                        currentImportState.updatedAt
                          ? (currentImportState.updatedAt | utcDate: 'dd-MM-yyyy HH:mm')
                          : '-'
                      }}
                    </p>
                  </div>
                </div>
                <div
                  class="row mt-3"
                  *ngIf="currentImportState.updatedBy"
                >
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Bijgewerkt door:</strong></p>
                    <p class="mb-0">{{ currentImportState.updatedBy }}</p>
                  </div>
                </div>
                <div class="row mt-3">
                  <div class="col-12">
                    <p class="mb-1"><strong>Opmerking:</strong></p>
                    <p class="mb-0">{{ currentImportState.comment === '' ? '-geen-' : currentImportState.comment }}</p>
                  </div>
                </div>
              </ng-container>
              <ng-template #importStateLoading>
                <div class="d-flex justify-content-center py-4">
                  <span class="spinner-border text-secondary"></span>
                </div>
              </ng-template>
            </div>
          </div>

          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">Importstaat bijwerken</h5>
            </div>
            <div class="card-body">
              <div class="d-flex flex-column">
                <div class="form-group pb-3">
                  <label for="importStateSelect">Nieuwe staat</label>
                  <select
                    class="form-select"
                    id="importStateSelect"
                    [(ngModel)]="selectedImportState"
                    name="importStateSelect"
                    [disabled]="isImportStateUpdating"
                  >
                    <option
                      value=""
                      disabled
                      hidden
                    >
                      Selecteer een staat
                    </option>
                    <option
                      *ngFor="let state of availableImportStates"
                      [value]="state"
                      [disabled]="
                        state === currentImportState?.state ||
                        state === productImportStateValues.MS_RUNNING_IMPORT ||
                        state === productImportStateValues.MS_ERROR_DURING_IMPORT
                      "
                    >
                      {{ getImportStateDisplayName(state) }}
                      <span *ngIf="state === currentImportState?.state"> (huidig)</span>
                    </option>
                  </select>
                </div>

                <div class="form-group pb-3">
                  <label for="importCommentInput">Opmerking (optioneel)</label>
                  <textarea
                    class="form-control"
                    id="importCommentInput"
                    rows="3"
                    [(ngModel)]="importComment"
                    name="importComment"
                    placeholder="Voeg een opmerking toe over deze importstaatswijziging..."
                    [disabled]="isImportStateUpdating"
                  ></textarea>
                </div>

                <div class="form-group align-self-end pt-1">
                  <button
                    class="btn btn-primary"
                    type="button"
                    [disabled]="!canUpdateImportState()"
                    (click)="updateImportState()"
                  >
                    <span
                      *ngIf="isImportStateUpdating"
                      class="spinner-border spinner-border-sm me-2"
                    ></span>
                    {{ isImportStateUpdating ? 'Bijwerken...' : 'Importstaat bijwerken' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</ng-container>

<div
  class="d-flex justify-content-center feedback_box"
  [ngClass]="{
    'd-none': !feedbackSettings.show,
  }"
>
  <app-feedback
    [(settings)]="feedbackSettings"
    (update)="returnHome()"
  ></app-feedback>
</div>

<div [ngClass]="{ 'd-none': !isLoading }">
  <app-loading-gears></app-loading-gears>
</div>
